import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Button, Flex } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { useTopicDraft } from "../../context/useTopicDraft";
import { useSimulator } from "../../context/SimulatorWrapper";
import { useGetInteractionAdditionalContextFieldsOptions } from "@/v2/entities/interaction-additional-context/InteractionAdditionalContext.hooks";
import { useGetSkills } from "@/v2/entities/skill/useGetSkills";
import { BriefEditor } from "./BriefEditor";
import { BriefTableOfContents, SearchResult } from "./BriefTableOfContents";
import styles from "./styles.module.scss";
import { BriefDialogProps } from "./BriefEditor.model";
import { useChat } from "../../context/ChatWrapper";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useGetTopicReleasePolicyId } from "@/v2/entities/topic-release-policy/useGetTopicReleasePolicy";

const BriefDialog = ({
  topicId,
  releaseId,
  open,
  openChange,
}: BriefDialogProps) => {
  const { data: topicReleasePolicyId, refetch: refetchTopicReleasePolicy } =
    useGetTopicReleasePolicyId(topicId, releaseId);

  const dialogContentRef = useRef<HTMLDivElement>(null);
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isTocCollapsed, setIsTocCollapsed] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const { topic, brief } = useTopicDraft({ topicId });
  const { handleBrief } = useSimulator();
  const { data: contextFieldsData } =
    useGetInteractionAdditionalContextFieldsOptions({
      topicId,
      topicReleasePolicyId: topicReleasePolicyId ?? "",
      enabled: topicReleasePolicyId != null,
    });
  const { data: allSkills } = useGetSkills();

  const skills = useMemo(() => {
    return allSkills || [];
  }, [allSkills]);

  const customFields = useMemo(() => {
    return contextFieldsData?.customFields || [];
  }, [contextFieldsData]);

  const enrichments = useMemo(() => {
    return contextFieldsData?.enrichments || [];
  }, [contextFieldsData]);

  useEffect(() => {
    setText(brief || "");
  }, [brief]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await handleBrief(text, topicId);
      openChange(false);
    } finally {
      setIsSaving(false);
    }
  }, [handleBrief, text, topicId, openChange]);

  const handleInteractOutside = (event: Event) => {
    if (
      event.target instanceof Element &&
      event.target.closest(".bubbleMenu")
    ) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  };

  const handleHeadingClick = useCallback((headingId: string) => {
    // Find the heading element in the editor
    const editorElement = editorWrapperRef.current;
    if (!editorElement) return;

    const headingElement = editorElement.querySelector(`#${headingId}`);
    if (headingElement) {
      headingElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Immediately set clicked heading as active
      setActiveHeadingId(headingId);

      // Optional: briefly highlight the heading
      headingElement.classList.add(styles.highlighted);
      setTimeout(() => {
        headingElement.classList.remove(styles.highlighted);
      }, 2000);
    }
  }, []);

  const handleToggleTocCollapse = useCallback(() => {
    setIsTocCollapsed(prev => !prev);
  }, []);

  const handleScrollToText = useCallback((searchText: string, result?: SearchResult) => {
    // Find the text in the rendered editor and scroll to it
    const editorElement = editorWrapperRef.current;
    if (!editorElement) return;

    // If we have a result with headingId, try to scroll to that heading first
    if (result?.headingId) {
      const headingElement = editorElement.querySelector(`#${result.headingId}`);
      if (headingElement) {
        headingElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        setActiveHeadingId(result.headingId);
        return;
      }
    }

    // Otherwise, search for the text in content
    // Get all text-containing elements
    const allElements = editorElement.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, td, th');

    // Search for the text (case-insensitive)
    const searchLower = searchText.toLowerCase();

    for (const element of Array.from(allElements)) {
      const textContent = element.textContent?.toLowerCase() || '';

      if (textContent.includes(searchLower)) {
        // Found the element containing our text
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // If it's a heading, set it as active
        if (element.tagName.match(/^H[1-6]$/)) {
          const id = element.getAttribute('id');
          if (id) {
            setActiveHeadingId(id);
          }
        }

        // Highlight the element
        element.classList.add(styles.highlighted);
        setTimeout(() => {
          element.classList.remove(styles.highlighted);
        }, 2000);

        break;
      }
    }
  }, []);


  // Track scroll position to update active heading
  useEffect(() => {
    const editorElement = editorWrapperRef.current;
    if (!editorElement) return;

    // Find the scroll container - the editor wrapper with overflow-y: auto
    const scrollContainer = editorElement.querySelector('.editor-wrapper') as HTMLElement;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Get all headings with IDs
      const headings = editorElement.querySelectorAll('h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]');
      if (headings.length === 0) {
        setActiveHeadingId(null);
        return;
      }

      const scrollTop = scrollContainer.scrollTop || 0;
      const containerRect = scrollContainer.getBoundingClientRect();
      const offset = 120; // Offset from top of viewport
      const threshold = scrollTop + offset;

      let activeId: string | null = null;

      // Find the heading that is at or just above the threshold
      // We iterate from bottom to top to find the last heading that's above the threshold
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i] as HTMLElement;
        const headingRect = heading.getBoundingClientRect();
        
        // Calculate heading's position relative to scroll container's content
        // headingRect.top is relative to viewport
        // containerRect.top is scroll container's position relative to viewport
        // scrollTop is how much we've scrolled
        // So: heading position in content = (headingRect.top - containerRect.top) + scrollTop
        const headingTopInContent = (headingRect.top - containerRect.top) + scrollTop;

        if (headingTopInContent <= threshold) {
          const id = heading.getAttribute('id');
          if (id) {
            activeId = id;
          }
          break;
        }
      }

      // If no heading found and we're at the very top, select first heading
      if (!activeId && scrollTop <= 50) {
        const firstHeading = headings[0] as HTMLElement;
        const firstHeadingId = firstHeading?.getAttribute('id');
        if (firstHeadingId) {
          activeId = firstHeadingId;
        }
      }

      // Only update if the active ID has changed
      if (activeId !== activeHeadingId) {
        setActiveHeadingId(activeId);
      }
    };

    // Initial check after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(handleScroll, 100);

    // Add scroll listener
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [text, activeHeadingId]);

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <div className={styles.briefDialog}>
        <Dialog.Content
          size="large"
          onInteractOutside={handleInteractOutside}
          onPointerDownOutside={handleInteractOutside}
        >
          <Dialog.Title>
            Add Brief to topic "{topic?.topic}"
            <Dialog.Close />
          </Dialog.Title>
          <div className={styles.briefContent} ref={dialogContentRef}>
            <div className={styles.briefContentWithToc}>
              <BriefTableOfContents
                content={text}
                onHeadingClick={handleHeadingClick}
                onScrollToText={handleScrollToText}
                isCollapsed={isTocCollapsed}
                onToggleCollapse={handleToggleTocCollapse}
                activeHeadingId={activeHeadingId}
              />
              <div className={styles.briefEditorWrapper} ref={editorWrapperRef}>
                <BriefEditor
                  value={text}
                  onChange={setText}
                  placeholder={`Write a brief for the ${topic?.topic} topic. Use @ to mention skills or ticket fields. Highlight text to format or link.`}
                  skills={skills}
                  customFields={customFields}
                  enrichments={enrichments}
                  dialogContentRef={dialogContentRef}
                />
              </div>
            </div>
            <Flex gap="3" justify="end" mt="24px" mb="24px" px="24px">
              <Button
                variant="ghost"
                color="gray"
                onClick={() => openChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="gray"
                highContrast
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                Save
              </Button>
            </Flex>
          </div>
        </Dialog.Content>
      </div>
    </Dialog>
  );
};

interface BriefDialogGuardProps {
  open: boolean;
  openChange: (value: boolean) => void;
}

export function BriefDialogGuard({ open, openChange }: BriefDialogGuardProps) {
  const simulatorValue = useSimulator();
  const { currentRelease, isLoadingCurrentRelease, currentReleaseError } =
    simulatorValue;

  const chatValue = useChat();
  const { selectedTopic } = chatValue;

  if (currentReleaseError) {
    toast.error("Something is wrong, release is not set");
    return <Navigate to="/" />;
  }

  if (
    selectedTopic == null ||
    isLoadingCurrentRelease ||
    currentRelease == null
  ) {
    return <></>;
  }

  return (
    <BriefDialog
      topicId={selectedTopic.id}
      releaseId={currentRelease.id}
      open={open}
      openChange={openChange}
    />
  );
}
